import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';

import { Alert, Loading } from '../../components';
import { Icon, StatePanel } from '../../components/ui';
import { UserContext, useEntitlement } from '../../contexts';
import { MASection } from './MASection';
import { YourPlanSection } from './YourPlanSection';
import { ChatMembersSection } from './RoamChatAdminSections';
import UserSearchSelect from '../../components/inputs/UserSearchSelect';
import { MarketplaceAccountsPanel } from '../molecules/MarketplaceAccountsPanel';
import { TeamInvitePanel } from '../molecules/TeamInvitePanel';
import { AddPersonForm } from '../molecules/AddPersonForm';
import { INVITE_LINKS_LIVE } from '../../core/config/invites';
import { CatalogFeedsPanel } from '../molecules/CatalogFeedsPanel';
import { ShippingProvidersPanel } from '../molecules/ShippingProvidersPanel';
import { XeroOrganisationsPanel } from '../molecules/XeroOrganisationsPanel';
import { StripeOwnAccountPanel } from '../molecules/StripeOwnAccountPanel';
import { AgreementSignatoryPanel } from '../molecules/AgreementSignatoryPanel';
import { BaseCurrencyPanel } from '../molecules/BaseCurrencyPanel';
import { StoreCatalogPanel } from '../molecules/StoreCatalogPanel';
import { WaNumbersPanel } from '../molecules/WaNumbersPanel';
import { InventoryLocationsPanel } from '../molecules/InventoryLocationsPanel';
import { ACCOUNTING_OPTIONS } from '../../helpers/accountingAccess';
import { ACCESS_OPTIONS, isActiveStoreOwner } from '../../helpers/adminAccess';

import './master-admin-settings.scss';

const IShield = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5L2.5 3.5V8C2.5 11 5 13.5 8 14.5C11 13.5 13.5 11 13.5 8V3.5L8 1.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
);
const IUsers = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="6" cy="5.5" r="2.3" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M2 13.5C2 11.3 3.8 9.5 6 9.5C8.2 9.5 10 11.3 10 13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    <circle cx="11.5" cy="6" r="1.8" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M14.5 12.5C14.5 10.8 13.2 9.5 11.5 9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);
const IEnvelope = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="3.5" width="13" height="9" rx="1" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M2 4.5L8 9L14 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
);
const ICard = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M1.5 6.5H14.5" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M4 10.5H7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

// Shell moved to its own module (circular-import guard: sections rendered on
// this page import it too). Re-exported here for existing importers.
export { MASection } from './MASection';

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function accessLabel(level) {
  if (level === 'read_write') return 'Read + Write';
  if (level === 'read') return 'Read only';
  return level || '—';
}

export function MCPAccessSection() {
  const [pending, setPending] = useState([]);
  const [verified, setVerified] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [auditFilters, setAuditFilters] = useState({ email: '', tool: '', from: '', to: '' });

  const reload = async () => {
    setLoading(true);
    setError('');
    try {
      const [pendingRes, verifiedRes, auditRes] = await Promise.all([
        axios.get('/api/admin/mcp/pending-requests').catch(() => ({ data: { items: [] } })),
        axios.get('/api/admin/mcp/verified-users').catch(() => ({ data: { items: [] } })),
        axios.get('/api/admin/mcp/audit', { params: { ...auditFilters, limit: 50 } }).catch(() => ({ data: { items: [] } })),
      ]);
      setPending(pendingRes.data?.items || []);
      setVerified(verifiedRes.data?.items || []);
      setAudit(auditRes.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const revoke = async (verifiedUserId) => {
    if (!window.confirm('Revoke MCP access for this user? Their token will be invalidated immediately.')) return;
    try {
      await axios.post(`/api/admin/mcp/revoke/${verifiedUserId}`);
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || 'Revoke failed');
    }
  };

  const reissue = async (requestId, action) => {
    try {
      await axios.post(`/api/admin/mcp/decide`, { request_id: requestId, action });
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || 'Action failed');
    }
  };

  const headerExtra = !loading
    ? <span className="ma-section__count">{pending.length} pending · {verified.length} verified</span>
    : null;

  return (
    <MASection title="MCP Access" icon={<IShield />} headerExtra={headerExtra}>
      <div className="ma-section__hint">
        Approve / revoke access to the Retail Circle MCP servers.
        <br />
        New users install once with{' '}
        <code>npm install -g github:Retail-Circle/RetailCircle-MCP</code>, then run{' '}
        <code>rc-mcp</code> to submit a request — that emails master admins a magic link.
        Returning users just re-run <code>rc-mcp</code> to claim their token after approval
        (link valid 7 days). Pending requests below can also be decided here.
      </div>

      {error && <Alert type="error" message="Something went wrong" description={error} />}
      {loading && <Loading />}

      {!loading && (
        <>
          <h4 className="ma-section__subhead">Pending requests · {pending.length}</h4>
          {pending.length === 0 ? (
            <p className="ma-section__empty">No pending requests.</p>
          ) : (
            <table className="ma-table">
              <thead>
                <tr>
                  <th>Requester</th><th>Email</th><th>Phone</th><th>Requested</th><th>Submitted</th><th>Decision</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((r) => (
                  <tr key={r.id}>
                    <td>{r.requester_name}</td>
                    <td>{r.requester_email}</td>
                    <td>{r.requester_phone}</td>
                    <td>{accessLabel(r.requested_access)}</td>
                    <td>{formatDateTime(r.created_at)}</td>
                    <td className="ma-table__actions">
                      <button type="button" className="rc-btn rc-btn--small" onClick={() => reissue(r.id, 'read')}>Read</button>
                      <button type="button" className="rc-btn rc-btn--small" onClick={() => reissue(r.id, 'write')}>Read+Write</button>
                      <button type="button" className="rc-btn rc-btn--small rc-btn--danger" onClick={() => reissue(r.id, 'deny')}>Decline</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h4 className="ma-section__subhead">Verified users · {verified.length}</h4>
          {verified.length === 0 ? (
            <p className="ma-section__empty">No verified users yet.</p>
          ) : (
            <table className="ma-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Access</th><th>Approved by</th><th>Approved at</th><th>Last used</th><th></th>
                </tr>
              </thead>
              <tbody>
                {verified.map((v) => (
                  <tr key={v.id}>
                    <td>{v.name}</td>
                    <td>{v.email}</td>
                    <td>{accessLabel(v.granted_access)}</td>
                    <td>{v.approved_by_name || '—'}</td>
                    <td>{formatDateTime(v.approved_at)}</td>
                    <td>{formatDateTime(v.last_used_at)}</td>
                    <td>
                      <button type="button" className="rc-btn rc-btn--small rc-btn--danger" onClick={() => revoke(v.id)}>Revoke</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h4 className="ma-section__subhead">Audit log · latest 50</h4>
          <div className="ma-filters">
            <input
              type="text" placeholder="email"
              value={auditFilters.email}
              onChange={(e) => setAuditFilters({ ...auditFilters, email: e.target.value })}
            />
            <input
              type="text" placeholder="tool name (e.g. searchProducts)"
              value={auditFilters.tool}
              onChange={(e) => setAuditFilters({ ...auditFilters, tool: e.target.value })}
            />
            <input
              type="date"
              value={auditFilters.from}
              onChange={(e) => setAuditFilters({ ...auditFilters, from: e.target.value })}
            />
            <input
              type="date"
              value={auditFilters.to}
              onChange={(e) => setAuditFilters({ ...auditFilters, to: e.target.value })}
            />
            <button type="button" className="rc-btn rc-btn--small" onClick={reload}>Apply</button>
          </div>
          {audit.length === 0 ? (
            <p className="ma-section__empty">No audit entries.</p>
          ) : (
            <table className="ma-table">
              <thead>
                <tr>
                  <th>When</th><th>Requester</th><th>Email</th><th>Tool</th><th>Status</th><th>Latency</th><th>IP</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id}>
                    <td>{formatDateTime(a.created_at)}</td>
                    <td>{a.requester_name || '—'}</td>
                    <td>{a.requester_email || '—'}</td>
                    <td><code>{a.tool_name}</code></td>
                    <td>{a.response_status}</td>
                    <td>{a.latency_ms != null ? `${a.latency_ms}ms` : '—'}</td>
                    <td>{a.ip_address}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </MASection>
  );
}

const ROLE_OPTIONS = [
  { value: 'owner',       label: 'Owner' },
  { value: 'store_admin', label: 'Store admin' },
  { value: 'sales_admin', label: 'Sales admin' },
];

/**
 * This store's team, from `store_user`.
 *
 * It used to call POST /api/users/search filtered by is_admin, which the
 * backend scopes by `users.store_id_m` — "whose USER RECORD lives in this
 * store" — not "who can operate in this store". Those are different questions,
 * and the multi-store rework rests on the difference.
 *
 * While master admins were unscoped, that query silently returned every admin
 * of every store no matter which store's settings you were on, so it looked
 * correct. Once a master could actually scope to a store it returned NOTHING
 * for any store nobody is seated in — e.g. DAO Watches Europe, whose admins
 * all have store_id_m = DAO Watches UK. They do have access (store_user rows);
 * the panel was just asking the wrong question.
 */
export function StoreAdminSection() {
  const { user, accessibleStores, activeStoreId } = useContext(UserContext);
  const [storeAdmins, setStoreAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [picked, setPicked] = useState(null);
  const [pickedRole, setPickedRole] = useState('sales_admin');

  // The store whose settings page this is. A master sitting on "Master Store"
  // (unscoped) has no single store in context, so there is no team to show —
  // handled explicitly below rather than silently listing the wrong one.
  const storeId = activeStoreId ?? user?.store_id_m ?? null;

  const reload = async () => {
    if (!storeId) {
      setStoreAdmins([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`/api/stores/${storeId}/members`);
      setStoreAdmins(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [storeId]);

  const grant = async () => {
    if (!picked || !storeId) return;
    setError('');
    setSuccess('');
    try {
      await axios.post(`/api/stores/${storeId}/members`, { user_id: picked.id, role: pickedRole });
      setSuccess('Access granted');
      setPicked(null);
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || 'Grant failed');
    }
  };

  const changeRole = async (userId, role) => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`/api/stores/${storeId}/members/${userId}`, { role });
      setSuccess('Role updated');
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || 'Update failed');
      await reload();
    }
  };

  const revoke = async (userId) => {
    setError('');
    setSuccess('');
    try {
      await axios.delete(`/api/stores/${storeId}/members/${userId}`);
      setSuccess('Access removed');
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || 'Remove failed');
    }
  };

  // Which store this person LANDS ON at login (users.default_store_id). One
  // store per user, so this reads as a radio across the team of THIS store:
  // ticking someone here makes this their landing store, unticking clears it
  // and sends them back to their store_id_m. It cannot be set for two stores
  // at once — setting it elsewhere silently replaces this one, which is why
  // the panel always re-reads from the server rather than assuming.
  const setDefaultStore = async (userId, isDefault) => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`/api/stores/${storeId}/members/${userId}/default`, {
        is_default: isDefault,
      });
      setSuccess(isDefault ? 'Default store set' : 'Default store cleared');
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || 'Update failed');
      await reload();
    }
  };

  // Per-admin "Access" capabilities (currently CRM Visibility, Remove Payment,
  // Create Product). Deliberately still a GLOBAL per-user setting, not
  // per-store — these are platform capabilities, not store membership, so they
  // keep using /api/users/{id}/admin-access. Optimistic, then persist.
  const setAdminAccess = async (userId, access) => {
    setError('');
    setSuccess('');
    setStoreAdmins((rs) => rs.map((r) => (r.user_id === userId ? { ...r, admin_access: access } : r)));
    try {
      await axios.put(`/api/users/${userId}/admin-access`, { access });
      setSuccess('Access updated');
    } catch (e) {
      setError(e.response?.data?.message || 'Update failed');
      await reload();
    }
  };

  const headerExtra = !loading
    ? <span className="ma-section__count">{storeAdmins.length} active</span>
    : null;

  return (
    <MASection anchor="store-admin" title="Store Admin" icon={<IUsers />} headerExtra={headerExtra}>
      <p className="ma-section__hint">
        Who can operate in <strong>this store</strong>. A person can hold access to several stores, with a different role in each — granting here does not affect their access anywhere else.<br />
        <strong>Owner</strong> — manages the store's team and settings.<br />
        <strong>Store admin</strong> — full operational access; cannot manage the team.<br />
        <strong>Sales admin</strong> — acts as a sales rep: can own Merchant/Expert/Client accounts, be assigned commission, and counts as internal sales staff in the P&amp;L.<br />
        <strong>Default</strong> — the store this person lands on when they log in. One store per person, so ticking it here replaces whatever store they defaulted to before; they can still switch stores at any time.
      </p>

      {error && <Alert type="error" message="Something went wrong" description={error} />}
      {success && <Alert type="success" message={success} />}

      {/* Adding by phone comes first: the picker below can only add someone who
          already has an account, which is no use to a merchant bringing in a
          colleague who has never heard of us. */}
      {storeId ? <AddPersonForm storeId={storeId} onAdded={reload} /> : null}

      {storeId && INVITE_LINKS_LIVE ? (
        <TeamInvitePanel
          storeId={storeId}
          storeName={accessibleStores?.find((s) => s.id === storeId)?.name ?? user?.store?.name}
          onChanged={reload}
        />
      ) : null}

      {!storeId ? (
        <p className="ma-section__empty">
          Select a specific store in the store switcher to manage its team — “Master Store” spans every store, so there is no single team to show.
        </p>
      ) : (
      <>
      <h4 className="rc-invite__title">Add someone who already has an account</h4>
      <div className="ma-grant">
        <UserSearchSelect
          placeholder="Search a user to grant access..."
          value={picked?.id || null}
          onChange={(opt) => setPicked(opt)}
        />
        <select
          className="ma-role-select"
          value={pickedRole}
          onChange={(e) => setPickedRole(e.target.value)}
          aria-label="Role to grant"
        >
          {ROLE_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <button
          type="button"
          className="rc-btn"
          disabled={!picked}
          onClick={grant}
        >
          Grant access
        </button>
      </div>

      {loading ? <Loading /> : (
        storeAdmins.length === 0 ? (
          <p className="ma-section__empty">Nobody has access to this store yet.</p>
        ) : (
          <table className="ma-table">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Phone</th><th>Type</th><th>Role</th><th>Access</th><th>Default</th><th />
              </tr>
            </thead>
            <tbody>
              {storeAdmins.map((m) => (
                <tr key={m.user_id}>
                  <td>{m.name}</td>
                  <td>{m.email}</td>
                  <td>{m.phone}</td>
                  <td>{m.type}</td>
                  <td>
                    <select
                      className="ma-role-select"
                      value={m.role}
                      // The store's real owner (stores.owner_id) is locked:
                      // store_user is derived from that record, so a change
                      // here would be undone by the next ownership sync.
                      disabled={m.is_store_owner_record}
                      title={m.is_store_owner_record ? 'Owns the store — transfer ownership to change this' : undefined}
                      onChange={(e) => changeRole(m.user_id, e.target.value)}
                      aria-label={`Role for ${m.name}`}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <AccessMultiSelect
                      options={ACCESS_OPTIONS}
                      placeholder="No access"
                      value={m.admin_access || []}
                      onChange={(access) => setAdminAccess(m.user_id, access)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!m.is_default}
                      onChange={(e) => setDefaultStore(m.user_id, e.target.checked)}
                      aria-label={`Make this store the default for ${m.name}`}
                      title="The store this person lands on when they log in. They can still switch."
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="rc-btn rc-btn--small"
                      disabled={m.is_store_owner_record}
                      title={m.is_store_owner_record ? 'Owns the store — transfer ownership to remove' : undefined}
                      onClick={() => revoke(m.user_id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
      </>
      )}
    </MASection>
  );
}

const IPercent = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="4.5" cy="4.5" r="2" stroke="currentColor" strokeWidth="1.3"/>
    <circle cx="11.5" cy="11.5" r="2" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M12.5 3.5L3.5 12.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

/**
 * Master Admin → Platform Fee: the application fee Retail Circle takes on each
 * connected (non-DAO) store's pay-by-link charges. Platform-level, master-admin
 * only. GET/PUT /api/admin/stripe/fee-config.
 */
export function StripeFeeSection() {
  const [enabled, setEnabled] = useState(true);
  const [percent, setPercent] = useState('0.25');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get('/api/admin/stripe/fee-config');
        if (!alive) return;
        setEnabled(!!data.enabled);
        setPercent(String(data.percent ?? '0.25'));
      } catch (e) {
        if (alive) setError('Could not load the platform fee config.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const save = async () => {
    const pct = parseFloat(percent);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      setError('Enter a percentage between 0 and 100.');
      return;
    }
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      const { data } = await axios.put('/api/admin/stripe/fee-config', { enabled, percent: pct });
      setEnabled(!!data.enabled);
      setPercent(String(data.percent));
      setSuccess('Platform fee updated.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update the platform fee.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MASection title="Platform Fee" icon={<IPercent />}>
      <p>
        The application fee Retail Circle takes on each connected store&apos;s
        pay-by-link payments. Applies to non-DAO stores only — DAO is exempt.
      </p>
      {loading ? (
        <Loading />
      ) : (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>Charge an application fee</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <span>Fee percentage</span>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={percent}
              disabled={!enabled}
              onChange={(e) => setPercent(e.target.value)}
              style={{ width: 90 }}
            />
            <span>%</span>
          </label>
          <button type="button" className="rc-btn rc-btn--small" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
      {success && <Alert type="success" message={success} />}
      {error && <Alert type="error" message={error} />}
    </MASection>
  );
}

const IKey = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="5" cy="6" r="3" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M7 8L13 14M11 12l1.5-1.5M13 14l1-1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Master Admin → Store Access: the manual paid-access flag behind the
 * EnsureMerchantAccess gate. Turning a store OFF revokes its API access once
 * the gate is enabled (RC_MERCHANT_ACCESS_GATE). The DAO/default store cannot
 * be gated. GET /api/admin/stores/billing, PATCH /api/admin/stores/{id}/billing.
 */
export function StoreBillingSection() {
  const [items, setItems] = useState([]);
  const [gateEnabled, setGateEnabled] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');

  const load = async (query = '') => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/admin/stores/billing', { params: query ? { q: query } : {} });
      setItems(data.items || []);
      setGateEnabled(!!data.gate_enabled);
    } catch (e) {
      setError('Could not load store access.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (store) => {
    setSavingId(store.id);
    setError('');
    try {
      const { data } = await axios.patch(`/api/admin/stores/${store.id}/billing`, {
        billing_active: !store.billing_active,
      });
      setItems((prev) => prev.map((s) => (s.id === store.id ? { ...s, billing_active: data.billing_active } : s)));
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update store access.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <MASection title="Store Access" icon={<IKey />}>
      <p>
        Manual paid-access control for each store. Turning a store OFF blocks its
        users from the API — but only once the access gate is switched on for
        this environment. The default store can never be gated.
      </p>
      {!gateEnabled && (
        <Alert
          type="info"
          message="Access gate is OFF"
          description="These toggles have no effect until RC_MERCHANT_ACCESS_GATE is enabled for this environment."
        />
      )}
      <form
        onSubmit={(e) => { e.preventDefault(); load(q.trim()); }}
        style={{ display: 'flex', gap: 8, margin: '10px 0' }}
      >
        <input
          type="text"
          value={q}
          placeholder="Search stores by name or id"
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1, textTransform: 'none' }}
        />
        <button type="submit" className="rc-btn rc-btn--small">Search</button>
      </form>
      {loading ? (
        <Loading />
      ) : (
        <div className="ma-store-billing">
          {items.length === 0 && <p>No stores found.</p>}
          {items.map((store) => (
            <div
              key={store.id}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}
            >
              <span style={{ flex: 1 }}>
                {store.display_name || store.name || `Store ${store.id}`}
                <span style={{ opacity: 0.5, marginLeft: 6 }}>#{store.id}</span>
                {store.is_default_store && <span style={{ opacity: 0.5, marginLeft: 6 }}>(default)</span>}
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={!!store.billing_active}
                  disabled={store.is_default_store || savingId === store.id}
                  onChange={() => toggle(store)}
                />
                <span>{store.billing_active ? 'Active' : 'Inactive'}</span>
              </label>
            </div>
          ))}
        </div>
      )}
      {error && <Alert type="error" message={error} />}
    </MASection>
  );
}

const IShieldCheck = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5L2.5 3.5V8C2.5 11 5 13.5 8 14.5C11 13.5 13.5 11 13.5 8V3.5L8 1.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M5.8 8L7.2 9.4L10.2 6.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/** Compact multi-select dropdown (checkbox popover). Defaults to the Accounting
 *  options; pass `options` to reuse it elsewhere (e.g. Store Admin CRM visibility). */
function AccessMultiSelect({ value, onChange, disabled, options = ACCOUNTING_OPTIONS, placeholder = 'Select access…' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const toggle = (key) => {
    onChange(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]);
  };
  const summary = value.length === 0
    ? placeholder
    : options.filter((o) => value.includes(o.key)).map((o) => o.label).join(', ');
  return (
    <div className={`ma-multiselect${open ? ' ma-multiselect--open' : ''}`} ref={ref}>
      <button type="button" className="ma-multiselect__btn" disabled={disabled} onClick={() => setOpen((o) => !o)} title={summary}>
        <span className="ma-multiselect__summary">{summary}</span>
        <span className="ma-multiselect__chev">▾</span>
      </button>
      {open && (
        <div className="ma-multiselect__menu">
          {options.map((o) => (
            <label key={o.key} className="ma-multiselect__opt">
              <input type="checkbox" checked={value.includes(o.key)} onChange={() => toggle(o.key)} />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function AccountingSection() {
  const [rows, setRows] = useState([]); // { id, first_name, last_name, email, phone, accounting_access:[] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [picked, setPicked] = useState(null);

  const reload = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/admin/accounting-users');
      setRows((data?.users || []).map((u) => ({ ...u, accounting_access: u.accounting_access || [] })));
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const save = async (userId, access) => {
    setError('');
    setSuccess('');
    setRows((rs) => rs.map((r) => (r.id === userId ? { ...r, accounting_access: access } : r)));
    try {
      await axios.put(`/api/users/${userId}/accounting-access`, { access });
      setSuccess('Accounting access updated');
    } catch (e) {
      setError(e.response?.data?.message || 'Update failed');
      await reload();
    }
  };

  const grant = () => {
    if (!picked) return;
    if (!rows.some((r) => r.id === picked.id)) {
      setRows((rs) => [{ id: picked.id, first_name: picked.name, last_name: '', email: '', phone: '', is_master_admin: false, store_name: '', accounting_access: [] }, ...rs]);
    }
    setPicked(null);
  };

  const remove = async (userId) => {
    await save(userId, []);
    setRows((rs) => rs.filter((r) => r.id !== userId));
  };

  const headerExtra = !loading
    ? <span className="ma-section__count">{rows.length} with access</span>
    : null;

  return (
    <MASection anchor="accounting" title="Accounting" icon={<IShieldCheck />} headerExtra={headerExtra}>
      <p className="ma-section__hint">
        Grant a user access to Accounting features. Add a user, then pick the options
        (Accounting tab, P&amp;L, Reconciliation, statement uploads, Void Invoice) from the dropdown.
        Master admins have full access by default and are listed here — you can still narrow
        their options.
      </p>

      {error && <Alert type="error" message="Something went wrong" description={error} />}
      {success && <Alert type="success" message={success} />}

      <div className="ma-grant">
        <UserSearchSelect
          placeholder="Search a user to grant Accounting access..."
          value={picked?.id || null}
          onChange={(opt) => setPicked(opt)}
        />
        <button type="button" className="rc-btn" disabled={!picked} onClick={grant}>
          Add user
        </button>
      </div>

      {loading ? <Loading /> : (
        rows.length === 0 ? (
          <p className="ma-section__empty">No users have Accounting access yet.</p>
        ) : (
          <table className="ma-table">
            <thead>
              <tr><th>Name</th><th>Store</th><th>Access</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td>
                    <span className="ma-acct-name">{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—'}</span>
                    {u.is_master_admin && <span className="ma-acct-badge">Master admin</span>}
                  </td>
                  <td>{u.store_name || '—'}</td>
                  <td>
                    <AccessMultiSelect
                      value={u.accounting_access || []}
                      onChange={(access) => save(u.id, access)}
                    />
                  </td>
                  <td>
                    {u.is_master_admin ? (
                      <span className="ma-acct-default" title="Master admins have full access by default">Default</span>
                    ) : (
                      <button type="button" className="rc-btn rc-btn--ghost" onClick={() => remove(u.id)}>Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </MASection>
  );
}

const FROM_EMAIL_DOMAIN = 'retail-circle.com';

const splitLocalPart = (full) => {
  if (!full) return '';
  const at = full.indexOf('@');
  return at === -1 ? full : full.slice(0, at);
};

const EMAIL_TEST_TYPES = [
  { value: 'USER_REGISTRATION', label: 'User Registration' },
  { value: 'PASSWORD_RESET', label: 'Password Reset' },
  { value: 'REQUEST_PROFILE_VERIFICATION', label: 'Request Profile Verification' },
  { value: 'RE_UPLOAD_KYC', label: 'Re-upload KYC' },
  { value: 'BIRTHDAY_WITH_PURCHASE', label: 'Birthday (with purchase)' },
  { value: 'BIRTHDAY_WITHOUT_PURCHASE', label: 'Birthday (without purchase)' },
  { value: 'USER_INVITATION', label: 'User Invitation' },
];

function EmailBrandingSection() {
  const [stores, setStores] = useState([]);
  const [configuredOnly, setConfiguredOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testingStoreId, setTestingStoreId] = useState(null);
  const [testType, setTestType] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [activeQuery, setActiveQuery] = useState('');

  const reload = async (q = '') => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/admin/stores/branding', { params: q ? { q } : {} });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setStores(items);
      setConfiguredOnly(data?.configured_only ?? !q);
      setActiveQuery(data?.query ?? q);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(''); }, []);

  // Debounced search — fire 350ms after the user stops typing.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput.trim() !== activeQuery) {
        reload(searchInput.trim());
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [searchInput]);

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 5000);
  };

  const startEdit = (store) => {
    setEditingId(store.id);
    setForm({
      display_name: store.display_name || '',
      from_email_name: store.from_email_name || '',
      from_email_local: splitLocalPart(store.from_email_address),
      reply_to_email: store.reply_to_email || '',
      support_email: store.support_email || '',
      physical_address: store.physical_address || '',
      social_url: store.social_url || '',
      store_url: store.store_url || '',
    });
    setLogoFile(null);
    setError('');
    setSuccess('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({});
    setLogoFile(null);
  };

  const save = async (storeId) => {
    setError('');
    setSaving(true);

    const fromLocal = (form.from_email_local || '').trim().toLowerCase();
    if (fromLocal && !/^[a-z0-9._-]+$/.test(fromLocal)) {
      setError('From address: only letters, digits, dot, dash, underscore allowed before the @.');
      setSaving(false);
      return;
    }

    try {
      const fd = new FormData();
      const payload = {
        display_name:        form.display_name,
        from_email_name:     form.from_email_name,
        from_email_address:  fromLocal ? `${fromLocal}@${FROM_EMAIL_DOMAIN}` : '',
        reply_to_email:      form.reply_to_email,
        support_email:       form.support_email,
        physical_address:    form.physical_address,
        social_url:          form.social_url,
        store_url:           form.store_url,
      };
      Object.entries(payload).forEach(([k, v]) => {
        if (v != null) fd.append(k, v);
      });
      if (logoFile) fd.append('logo', logoFile, logoFile.name);

      await axios.post(`/api/admin/stores/${storeId}/branding`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      flashSuccess('Branding saved');
      cancelEdit();
      await reload(activeQuery);
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (storeId) => {
    const type = testType[storeId] || EMAIL_TEST_TYPES[0].value;
    setError('');
    setTestingStoreId(storeId);
    try {
      await axios.post(`/api/admin/stores/${storeId}/branding/test-email`, {
        email_type: type,
      });
      flashSuccess(`Test ${type} sent to your inbox`);
    } catch (e) {
      setError(e.response?.data?.message || 'Test send failed');
    } finally {
      setTestingStoreId(null);
    }
  };

  const headerExtra = !loading
    ? <span className="ma-section__count">{stores.length} {configuredOnly ? 'configured' : 'matches'}</span>
    : null;

  return (
    <MASection anchor="email-branding" title="Email Branding" icon={<IEnvelope />} headerExtra={headerExtra}>
      <p className="ma-section__hint">
        Per-store transactional email branding. Logo and merge variables are injected
        into every Loops template that references them.
        <br />
        By default only stores with branding already configured are shown.
        Type below to search every store by name, display name, From address, or numeric ID.
      </p>

      <div className="ma-eb-search">
        <input
          type="search"
          placeholder="Search stores (e.g. dao, acme, 873)…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="ma-eb-search__input"
        />
        {searchInput && (
          <button
            type="button"
            className="rc-btn rc-btn--small"
            onClick={() => { setSearchInput(''); }}
          >
            Clear
          </button>
        )}
      </div>

      {error && <Alert type="error" message="Something went wrong" description={error} />}
      {success && <Alert type="success" message={success} />}
      {loading && <Loading />}

      {!loading && stores.length === 0 && (
        <p className="ma-section__empty">
          {activeQuery
            ? `No stores match "${activeQuery}".`
            : 'No stores configured yet. Search for one above to start.'}
        </p>
      )}

      {!loading && stores.length > 0 && (
        <div className="ma-eb-list">
          {stores.map((s) => {
            const isEditing = editingId === s.id;
            const isTesting = testingStoreId === s.id;
            const overrides = s.email_template_overrides || {};
            const overrideCount = Object.keys(overrides).length;
            return (
              <div key={s.id} className={`ma-eb-card${isEditing ? ' ma-eb-card--editing' : ''}`}>
                <div className="ma-eb-card__head">
                  <div className="ma-eb-card__logo">
                    {s.logo_url
                      ? <img src={s.logo_url} alt="" />
                      : <span className="ma-eb-card__logo-empty">no logo</span>}
                  </div>
                  <div className="ma-eb-card__ident">
                    <div className="ma-eb-card__name">
                      {s.name}
                      {s.is_master_admin_store && <span className="ma-eb-card__badge">master</span>}
                    </div>
                    <div className="ma-eb-card__meta">
                      <code>id {s.id}</code>
                      {s.display_name && <span>· {s.display_name}</span>}
                    </div>
                  </div>
                  <div className="ma-eb-card__actions">
                    <select
                      className="ma-eb-card__type"
                      value={testType[s.id] || EMAIL_TEST_TYPES[0].value}
                      onChange={(e) => setTestType({ ...testType, [s.id]: e.target.value })}
                    >
                      {EMAIL_TEST_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="rc-btn rc-btn--small"
                      disabled={isTesting}
                      onClick={() => sendTest(s.id)}
                    >
                      {isTesting ? '…' : 'Send test'}
                    </button>
                    <button
                      type="button"
                      className="rc-btn rc-btn--small"
                      onClick={() => (isEditing ? cancelEdit() : startEdit(s))}
                    >
                      {isEditing ? 'Close' : 'Edit'}
                    </button>
                  </div>
                </div>

                <dl className="ma-eb-card__fields">
                  <div>
                    <dt>From</dt>
                    <dd>
                      {s.from_email_name && <strong>{s.from_email_name} </strong>}
                      {s.from_email_address
                        ? <span className="ma-eb-card__mono">&lt;{s.from_email_address}&gt;</span>
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>Reply-To</dt>
                    <dd>{s.reply_to_email
                      ? <span className="ma-eb-card__mono">{s.reply_to_email}</span>
                      : '—'}</dd>
                  </div>
                  <div>
                    <dt>Support</dt>
                    <dd>{s.support_email
                      ? <span className="ma-eb-card__mono">{s.support_email}</span>
                      : '—'}</dd>
                  </div>
                  <div>
                    <dt>Website</dt>
                    <dd>{s.store_url
                      ? <a href={s.store_url} target="_blank" rel="noreferrer">{s.store_url}</a>
                      : '—'}</dd>
                  </div>
                  <div>
                    <dt>Social</dt>
                    <dd>{s.social_url
                      ? <a href={s.social_url} target="_blank" rel="noreferrer">{s.social_url}</a>
                      : '—'}</dd>
                  </div>
                  <div>
                    <dt>Address</dt>
                    <dd>{s.physical_address || '—'}</dd>
                  </div>
                  {overrideCount > 0 && (
                    <div>
                      <dt>Overrides</dt>
                      <dd>{overrideCount} template{overrideCount === 1 ? '' : 's'} (advanced)</dd>
                    </div>
                  )}
                </dl>

                {isEditing && (
                  <div className="ma-eb-card__form">
                    <div className="ma-eb-card__form-grid">
                      <label>
                        <div className="ma-eb-card__label">Store name (shown in emails)</div>
                        <div className="ma-eb-card__hint">The name clients see in the body of your emails.</div>
                        <input
                          type="text"
                          value={form.display_name}
                          onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                          placeholder="e.g. Acme Watches"
                        />
                      </label>
                      <label>
                        <div className="ma-eb-card__label">Logo (PNG/SVG, dark-bg friendly)</div>
                        <div className="ma-eb-card__hint">Shown at the top of every email you send.</div>
                        <input
                          type="file"
                          accept="image/png,image/svg+xml,image/jpeg"
                          onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                        />
                        {logoFile && <div className="ma-eb-card__hint">{logoFile.name}</div>}
                      </label>
                      <label>
                        <div className="ma-eb-card__label">From name (inbox sender) *</div>
                        <div className="ma-eb-card__hint">The sender name in your client’s inbox. Completes the email step on your checklist.</div>
                        <input
                          type="text"
                          value={form.from_email_name}
                          onChange={(e) => setForm({ ...form, from_email_name: e.target.value })}
                          placeholder="e.g. Acme Watches"
                        />
                      </label>
                      <label>
                        <div className="ma-eb-card__label">From address</div>
                        <div className="ma-eb-card__email-input">
                          <input
                            type="text"
                            value={form.from_email_local}
                            onChange={(e) => setForm({ ...form, from_email_local: e.target.value })}
                            placeholder="yourstore"
                            autoComplete="off"
                            spellCheck={false}
                          />
                          <span className="ma-eb-card__email-suffix">@{FROM_EMAIL_DOMAIN}</span>
                        </div>
                        <div className="ma-eb-card__hint">Verified sending domain — local part only.</div>
                      </label>
                      <label>
                        <div className="ma-eb-card__label">Reply-To *</div>
                        <div className="ma-eb-card__hint">Where replies go. Completes the email step on your checklist.</div>
                        <input
                          type="email"
                          value={form.reply_to_email}
                          onChange={(e) => setForm({ ...form, reply_to_email: e.target.value })}
                          placeholder="sales@yourstore.com"
                        />
                      </label>
                      <label>
                        <div className="ma-eb-card__label">Support email</div>
                        <div className="ma-eb-card__hint">Shown to clients who need help. Optional.</div>
                        <input
                          type="email"
                          value={form.support_email}
                          onChange={(e) => setForm({ ...form, support_email: e.target.value })}
                          placeholder="support@yourstore.com"
                        />
                      </label>
                      <label className="ma-eb-card__span2">
                        <div className="ma-eb-card__label">Address (footer)</div>
                        <div className="ma-eb-card__hint">Your trading address in the email footer.</div>
                        <input
                          type="text"
                          value={form.physical_address}
                          onChange={(e) => setForm({ ...form, physical_address: e.target.value })}
                          placeholder="London, United Kingdom"
                        />
                      </label>
                      <label className="ma-eb-card__span2">
                        <div className="ma-eb-card__label">Website URL ({'{{storeUrl}}'} in emails)</div>
                        <input
                          type="url"
                          value={form.store_url}
                          onChange={(e) => setForm({ ...form, store_url: e.target.value })}
                          placeholder="https://yourstore.com"
                        />
                      </label>
                      <label className="ma-eb-card__span2">
                        <div className="ma-eb-card__label">Social URL</div>
                        <div className="ma-eb-card__hint">Linked from the email footer. Optional.</div>
                        <input
                          type="url"
                          value={form.social_url}
                          onChange={(e) => setForm({ ...form, social_url: e.target.value })}
                          placeholder="https://www.instagram.com/dao.watches/"
                        />
                      </label>
                    </div>
                    <div className="ma-eb-card__form-actions">
                      <button type="button" className="rc-btn" disabled={saving} onClick={() => save(s.id)}>
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                      <button type="button" className="rc-btn rc-btn--small" onClick={cancelEdit} disabled={saving}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </MASection>
  );
}

export function MasterAdminSettings() {
  const { user, accessibleStores, activeStoreId } = useContext(UserContext);
  // The Email Branding section belongs to the CRM row "Custom branded
  // transactional emails" — NOT the Platform "In-App branding" row, which is
  // a separate (Future Release) feature about branding inside the app.
  const hasCustomBranding = useEntitlement('branded_transactional_emails');
  const hasXeroPo = useEntitlement('xero_purchase_orders');
  const hasXeroInvoicing = useEntitlement('xero_invoicing');
  const hasXeroSection = hasXeroPo || hasXeroInvoicing;
  const hasStripePayments = useEntitlement('stripe_payments');
  // The "WhatsApp Broadcast" row (key kept from the shared-number row).
  const hasWaBroadcast = useEntitlement('wa_shared_number');
  // Shipping labels & parcel tracking (Admin Assistant). Configuring a
  // carrier here is the starting point for every label and every tracking
  // number, so hiding this panel IS the gate — the same reasoning as the
  // WhatsApp tab for wa_group_ingestion.
  const hasShipping = useEntitlement('shipping_labels_tracking');

  if (!user) return <Loading />;
  // Primary Admin = the store owner's per-store settings page. Open it to the
  // store owner (Primary admin) as well as RC master admins — it was previously
  // gated on is_master_admin only, so a store's own Primary admin got "Access
  // denied" on their own settings.
  //
  // Gated on the ACTIVE store's role (isActiveStoreOwner), not the raw
  // is_store_owner flag (home store) — a multi-store owner who switches into
  // a store they don't personally own must NOT see that store's Primary
  // Admin page just because they own a different one, and an owner acting in
  // their own store must see it even if is_store_owner reflects a different
  // home store. Mirrors the backend's User::isPrimaryAdmin(), which already
  // resolves the same way for a user checking their own access.
  if (!isActiveStoreOwner(user, accessibleStores, activeStoreId)) {
    return (
      <div className="master-admin-denied">
        <StatePanel state="permission" headingLevel={2} />
      </div>
    );
  }

  return (
    <div className="master-admin">
      <h2>Owner Admin</h2>
      <p className="master-admin__intro">
        Your store&apos;s own controls: plan, catalog feeds, accounting, email
        branding, marketplace accounts, shipping, payments and Xero.
      </p>

      <div className="ma-sections">
        {/* Store Admin moved HERE from Retail Circle Admin (Alex 2026-09-01):
            it is the store's own team panel and the backend has always let
            the store's owner manage it (StoreMemberController::authorizeManage).
            MCP Access stays platform governance on /retail-circle-admin. */}
        <StoreAdminSection />
        {/* Who from this store sits in the chat group with Retail Circle. */}
        <ChatMembersSection />
        <YourPlanSection />
        {/* Store-catalog feature: the store's own brands/models/products,
            merged with the shared RC catalogue and (unless auto-approve is
            on) held for RC approval. Backend: .claude/features/store-catalog.md
            in retail-circle.com. */}
        <MyProductsSection />
        <CatalogFeedsSection />
        <BaseCurrencySection user={user} />
        <InventoryLocationsSection />
        <AccountingSection />
        {/* Custom branded transactional emails (Feature 31). Hidden without
            the plan; the store's saved branding is preserved in the database
            and simply not applied — emails go out on Retail Circle defaults
            until the feature returns. */}
        {hasCustomBranding && <EmailBrandingSection />}
        <AgreementSignatorySection user={user} />
        <MarketplaceAccountsSection user={user} />
        {hasShipping && <ShippingProvidersSection user={user} />}
        {/* Xero Organisations is needed for Xero POs AND Xero invoicing, so
            it shows when EITHER is in the plan (Alex, 2026-08-13). */}
        {hasXeroSection && <XeroOrganisationsSection />}
        {hasStripePayments && <PaymentsSection user={user} />}
        {hasWaBroadcast && <WaNumbersSection />}
      </div>
    </div>
  );
}

function InventoryLocationsSection() {
  return (
    <MASection anchor="inventory-locations" title="Inventory Locations" icon={<Icon name="MapPin" size="sm" />}>
      <InventoryLocationsPanel />
    </MASection>
  );
}

function WaNumbersSection() {
  return (
    <MASection anchor="whatsapp-broadcast" title="WhatsApp Broadcast" icon={<IUsers />}>
      <WaNumbersPanel />
    </MASection>
  );
}

const IShoppingBag = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M3 5h10l-1 9H4L3 5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M6 5V3.5a2 2 0 014 0V5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

const IWatch = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="4.2" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M8 6v2l1.5 1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    <path d="M6 3.8L6.4 1.5h3.2L10 3.8M6 12.2l.4 2.3h3.2l.4-2.3" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
);

// The store's own catalogue entries (store-catalog feature).
function MyProductsSection() {
  return (
    <MASection title="My Products" icon={<IWatch />}>
      <StoreCatalogPanel />
    </MASection>
  );
}

function MarketplaceAccountsSection({ user }) {
  return (
    <MASection anchor="marketplace-accounts" title="Marketplace Accounts" icon={<IShoppingBag />}>
      <MarketplaceAccountsPanel user={user} />
    </MASection>
  );
}

const IFeed = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="3.5" cy="12.5" r="1.3" fill="currentColor"/>
    <path d="M2.5 8.5C4.7 8.5 6.5 10.3 6.5 12.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    <path d="M2.5 4.5C7 4.5 10.5 8 10.5 12.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    <path d="M2.5 1.5C8.6 1.5 13.5 6.4 13.5 12.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

// Self-serve WhatsApp/Meta + Google Shopping catalog feeds (Feature 5).
function CatalogFeedsSection() {
  return (
    <MASection anchor="catalog-feeds" title="Catalog Feeds" icon={<IFeed />}>
      <CatalogFeedsPanel />
    </MASection>
  );
}

const IPen = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M11 2.5l2.5 2.5L6 12.5 3 13l.5-3L11 2.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M9.5 4l2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

function AgreementSignatorySection({ user }) {
  return (
    <MASection anchor="agreement-signatory" title="Agreement Signatory" icon={<IPen />}>
      <AgreementSignatoryPanel user={user} />
    </MASection>
  );
}

const ITruck = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M1.5 3.5h8v7h-8v-7z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M9.5 6h3l2 2.5v2h-5V6z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <circle cx="4.5" cy="12" r="1.4" stroke="currentColor" strokeWidth="1.2"/>
    <circle cx="11.5" cy="12" r="1.4" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);

function ShippingProvidersSection({ user }) {
  return (
    <MASection anchor="shipping-providers" title="Shipping Providers" icon={<ITruck />}>
      <ShippingProvidersPanel user={user} />
    </MASection>
  );
}

function PaymentsSection({ user }) {
  return (
    <MASection anchor="payments" title="Payments" icon={<ICard />}>
      {/* Stripe Link → the store's own Stripe account (the onboarding step). */}
      <StripeOwnAccountPanel user={user} />
      {/* StripeConnectPanel is deliberately NOT mounted (Alex, 2026-10-13):
          Connect is reserved for the future storefront-checkout feature (RC
          checkout + commission + transfer to the merchant). Payment links use
          the merchant's own key above. Re-mount it when that feature ships. */}
    </MASection>
  );
}

const ICoin = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.3" />
    <path d="M8 4.8v6.4M6.4 6.2h2.4a1.2 1.2 0 010 2.4H6.6h2.2a1.2 1.2 0 010 2.4H6.4"
          stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function BaseCurrencySection({ user }) {
  return (
    <MASection anchor="base-currency" title="Base Currency" icon={<ICoin />} defaultOpen>
      <BaseCurrencyPanel user={user} />
    </MASection>
  );
}

const IXero = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.3" />
    <path d="M6 6l4 4M10 6l-4 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

function XeroOrganisationsSection() {
  return (
    <MASection anchor="xero-organisations" title="Xero Organisations" icon={<IXero />}>
      <XeroOrganisationsPanel />
    </MASection>
  );
}
